import { useState } from 'react';
import clsx from 'clsx';

export type SectionStatus = 'pending' | 'partial' | 'complete' | 'error';

export interface SectionNavItem {
  id: string;
  title: string;
  status: SectionStatus;
  progress?: { answered: number; total: number };
}

export interface SectionNavProps {
  sections: SectionNavItem[];
  activeId: string;
  onJump: (id: string) => void;
  className?: string;
}

/**
 * Left-rail (desktop) + sticky top bar (mobile) progress navigator for the
 * adaptive property form. See docs/property/PRD.md §8.4 and the Problem 1
 * proposal in the PRD design notes.
 *
 * Users can jump to any section — the form supports save-and-return and
 * non-linear navigation, so gating section access by predecessor completion
 * would fight the UX pattern.
 */
export function SectionNav({
  sections,
  activeId,
  onJump,
  className,
}: SectionNavProps) {
  return (
    <>
      <DesktopRail
        sections={sections}
        activeId={activeId}
        onJump={onJump}
        className={className}
      />
      <MobileBar
        sections={sections}
        activeId={activeId}
        onJump={onJump}
      />
    </>
  );
}

function DesktopRail({
  sections,
  activeId,
  onJump,
  className,
}: SectionNavProps) {
  return (
    <nav
      aria-label="Form progress"
      className={clsx(
        'hidden lg:flex flex-col gap-1 sticky top-24 self-start w-[240px]',
        className
      )}
    >
      {sections.map((s, i) => (
        <SectionRow
          key={s.id}
          index={i + 1}
          section={s}
          active={s.id === activeId}
          onClick={() => onJump(s.id)}
        />
      ))}
    </nav>
  );
}

function MobileBar({ sections, activeId, onJump }: SectionNavProps) {
  const [open, setOpen] = useState(false);
  const active = sections.find((s) => s.id === activeId) ?? sections[0];
  const activeIdx = sections.findIndex((s) => s.id === activeId);
  const total = sections.length;

  if (!active) return null;

  return (
    <div className="lg:hidden sticky top-[64px] z-20 bg-canvas/95 backdrop-blur-sm border-b border-hairline -mx-[var(--gutter)] px-[var(--gutter)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="section-nav-sheet"
        className="w-full flex items-center justify-between py-3 text-left"
      >
        <span className="flex flex-col">
          <span className="font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle">
            &gt; STEP_{String(activeIdx + 1).padStart(2, '0')} · OF_{String(total).padStart(2, '0')}
          </span>
          <span className="font-display text-[0.9375rem] text-ink mt-0.5">
            {active.title}
          </span>
        </span>
        <span
          className={clsx(
            'font-mono text-[0.75rem] text-ink-subtle transition-transform',
            open ? 'rotate-90' : ''
          )}
          aria-hidden="true"
        >
          ›
        </span>
      </button>
      {open ? (
        <div id="section-nav-sheet" className="py-2 pb-4 flex flex-col gap-1">
          {sections.map((s, i) => (
            <SectionRow
              key={s.id}
              index={i + 1}
              section={s}
              active={s.id === activeId}
              onClick={() => {
                onJump(s.id);
                setOpen(false);
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SectionRow({
  index,
  section,
  active,
  onClick,
}: {
  index: number;
  section: SectionNavItem;
  active: boolean;
  onClick: () => void;
}) {
  const label = String(index).padStart(2, '0');
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'step' : undefined}
      className={clsx(
        'group relative flex items-center justify-between px-3 py-2 text-left rounded-md transition-colors',
        active
          ? 'bg-blue-whisper/50'
          : 'hover:bg-panel'
      )}
    >
      {active ? (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[2px] rounded-full bg-gradient-primary"
        />
      ) : null}
      <span className="flex items-baseline gap-3">
        <span className="font-mono text-[0.6875rem] tracking-[0.08em] text-ink-subtle">
          &gt;&nbsp;{label}
        </span>
        <span className="font-display text-[0.875rem] text-ink leading-snug">
          {section.title}
        </span>
      </span>
      <StatusIndicator status={section.status} progress={section.progress} />
    </button>
  );
}

function StatusIndicator({
  status,
  progress,
}: {
  status: SectionStatus;
  progress?: { answered: number; total: number };
}) {
  if (status === 'complete') {
    return (
      <span
        aria-label="complete"
        className="font-mono text-[0.75rem] text-blue-deep"
      >
        ✓
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span
        aria-label="has error"
        className="font-mono text-[0.75rem] text-[#b33a3a]"
      >
        !
      </span>
    );
  }
  if (status === 'partial' && progress) {
    return (
      <span className="font-mono text-[0.6875rem] tabular-nums text-ink-subtle">
        {progress.answered}/{progress.total}
      </span>
    );
  }
  return (
    <span
      aria-label="pending"
      className="font-mono text-[0.75rem] text-ink-subtle"
    >
      —
    </span>
  );
}
